import { useState } from "react";
import { StoreTransactionsData } from "../../services/TransactionServices";
import styles from "./StatBox.module.css";
import storeStyles from "../../pages/Store/Store.module.css";
import { transactionStatusEnum } from "../../enums/transactionStatusEnum";
import globalStyles from "../../styles/globalStyles.module.css";

interface StatBoxProps {
  transactions: (StoreTransactionsData & { type: string })[];
  onMonthChange: (month: number) => void;
  onTodayClick: () => void;
}

const StatBox: React.FC<StatBoxProps> = ({
  transactions,
  onMonthChange,
  onTodayClick,
}) => {
  const [monthOffset, setMonthOffset] = useState(0); // 0 = current, -1 = previous
  const stats: SalesStats = getSalesStats(transactions, monthOffset);

  const currentDate = new Date();

  // Find min and max year/month in transactions
  const months = transactions.map((tx) => {
    const d = new Date(tx.updated_at);
    return d.getFullYear() * 12 + d.getMonth();
  });

  const minMonth = months.length
    ? Math.min(...months)
    : currentDate.getFullYear() * 12 + currentDate.getMonth();

  const maxMonth = months.length
    ? Math.max(...months)
    : currentDate.getFullYear() * 12 + currentDate.getMonth();

  const currentMonthIndex =
    currentDate.getFullYear() * 12 + currentDate.getMonth();
  const viewMonthIndex = currentMonthIndex + monthOffset;

  const canGoPrev = viewMonthIndex > minMonth;
  const canGoNext = viewMonthIndex < maxMonth;
  // const canGoPrev = true;
  // const canGoNext = true;

  const viewDate = new Date(
    currentDate.getFullYear(),
    currentDate.getMonth() + monthOffset,
    1
  );

  const filtered = transactions.filter((tx) => {
    const txDate = new Date(tx.updated_at);
    return (
      txDate.getMonth() === viewDate.getMonth() &&
      txDate.getFullYear() === viewDate.getFullYear()
    );
  });

  const countByStatus = (status: transactionStatusEnum) =>
    filtered.filter((tx) => tx.status === status).length;

  const amountByStatus = (status: transactionStatusEnum) => {
    const subSet = filtered.filter((tx) => tx.status === status);
    return subSet
      .reduce((sum, tx) => sum + Number(tx.net_amount), 0)
      .toFixed(2);
  };

  const handleCurrentMonthChange = (offset: number) => {
    onMonthChange(offset);
    setMonthOffset((prev) => prev + offset);
  };

  const handleTodayClick = () => {
    setMonthOffset(0);
    onTodayClick();
  };

  return (
    <div className={styles.statBox}>
      <div className={styles.header}>
        <button
          onClick={() => handleCurrentMonthChange(-1)}
          disabled={!canGoPrev}
        >
          &lt;
        </button>

        <div className={styles.numberCard}>
          <h5 className={styles.month}>{stats.lastMonth.date}</h5>
          <h4>€{stats.lastMonth.amount.toFixed(2)}</h4>
        </div>
        <div className={styles.numberCard}>
          <div className={storeStyles.tooltipWrapper}>
            <h5 className={styles.month}>{stats.thisMonth.date}</h5>
            <h2>€{stats.thisMonth.amount.toFixed(2)}</h2>
            <span className={storeStyles.tooltipText}>
              This is the amount currently available for transfer. You must wait{" "}
              <strong>14 days</strong> after a sale to transfer the earnings.
            </span>
          </div>
        </div>
        <div className={styles.numberCard}>
          <h5 className={styles.month}>{stats.nextMonth.date}</h5>
          <h4>€{stats.nextMonth.amount.toFixed(2)}</h4>
        </div>
        <button
          onClick={() => handleCurrentMonthChange(1)}
          disabled={!canGoNext}
        >
          &gt;
        </button>
      </div>
      <div className={styles.todayBtn}>
        <button
          onClick={handleTodayClick}
          style={{ marginLeft: 8 }}
          className={`${globalStyles.button}`}
        >
          Today
        </button>
      </div>
      <div className={styles.stats}>
        <div className={styles.statItem}>
          <p>New Orders</p>
          <div className={storeStyles.tooltipWrapper}>
            <h3>{countByStatus(transactionStatusEnum.COMPLETED)}</h3>
            <div>{amountByStatus(transactionStatusEnum.COMPLETED)}€</div>
            <span className={storeStyles.tooltipText}>
              Total amount of new orders completed this month. Fund from a
              transation is available for transfer after{" "}
              <strong>14 days</strong>
            </span>
          </div>
        </div>
        <div className={styles.statItem}>
          <p>Transfered</p>
          <div className={storeStyles.tooltipWrapper}>
            <h3>{countByStatus(transactionStatusEnum.TRANSFERRED)}</h3>
            <div>{amountByStatus(transactionStatusEnum.TRANSFERRED)}€</div>
            <span className={storeStyles.tooltipText}>
              Total Amount transfered to your stripe acccount this month.
            </span>
          </div>
        </div>
        <div className={styles.statItem}>
          <p>Complaints</p>
          <div className={storeStyles.tooltipWrapper}>
            <h3>{countByStatus(transactionStatusEnum.REFUNDED)}</h3>
            <div>{amountByStatus(transactionStatusEnum.REFUNDED)}€</div>
            <span className={storeStyles.tooltipText}>
              Total amount refunded this month. This amount is deducted from
              your earnings.
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

interface SalesStats {
  thisMonth: { amount: number; date: string };
  lastMonth: { amount: number; date: string };
  nextMonth: { amount: number; date: string };
}

function getSalesStats(
  transactions: (StoreTransactionsData & { type: string })[],
  offset: number
): SalesStats {
  const now = new Date();
  const targetDate = new Date(now.getFullYear(), now.getMonth() + offset, 1);
  const currentYear = targetDate.getFullYear();
  const currentMonth = targetDate.getMonth(); // 0-indexed (0 = Jan)

  let thisMonthTotal = 0;
  let lastMonthTotal = 0;
  let nextMonthTotal = 0;
  const salesByDate: Record<string, number> = {};

  transactions.forEach((tx) => {
    if (
      tx.status !== transactionStatusEnum.COMPLETED &&
      tx.status !== transactionStatusEnum.TRANSFERRED
    )
      return;

    const dateObj = new Date(tx.updated_at);
    const year = dateObj.getFullYear();
    const month = dateObj.getMonth();
    const day = dateObj.toISOString().split("T")[0];

    const revenue = Number(tx.net_amount);

    if (year === currentYear && month === currentMonth) {
      thisMonthTotal += revenue;
    }

    // Last month (handle wrap around December/January)
    const lastMonthDate = new Date(currentYear, currentMonth - 1, 1);
    if (
      year === lastMonthDate.getFullYear() &&
      month === lastMonthDate.getMonth()
    ) {
      lastMonthTotal += revenue;
    }

    // Next month (handle wrap around December/January)
    const nextMonthDate = new Date(currentYear, currentMonth + 1, 1);
    if (
      year === nextMonthDate.getFullYear() &&
      month === nextMonthDate.getMonth()
    ) {
      nextMonthTotal += revenue;
    }

    if (!salesByDate[day]) salesByDate[day] = 0;
    salesByDate[day] += revenue;
  });

  const formatMonth = (year: number, month: number) =>
    new Date(year, month, 1).toLocaleString("default", { month: "long" });

  const thisMonth = {
    amount: thisMonthTotal,
    date: formatMonth(currentYear, currentMonth),
  };

  const lastMonth = {
    amount: lastMonthTotal,
    date: formatMonth(
      new Date(currentYear, currentMonth - 1, 1).getFullYear(),
      new Date(currentYear, currentMonth - 1, 1).getMonth()
    ),
  };

  const nextMonth = {
    amount: nextMonthTotal,
    date: formatMonth(
      new Date(currentYear, currentMonth + 1, 1).getFullYear(),
      new Date(currentYear, currentMonth + 1, 1).getMonth()
    ),
  };

  return {
    thisMonth,
    lastMonth,
    nextMonth,
  };
}

export default StatBox;
